/**
 * Events Feed JavaScript
 * Handles fetching, rendering, and modal interactions for calendar events
 */

// Global EventsFeed object to prevent conflicts
window.EventsFeed = window.EventsFeed || {};

(function() {
  'use strict';

  const EventsFeed = {
    sections: new Map(),

    /**
     * Initialize all events feed sections on the page
     */
    init() {
      document.addEventListener('DOMContentLoaded', () => {
        const sections = document.querySelectorAll('.events-feed-section');
        sections.forEach(section => {
          this.initSection(section);
        });
      });

      // Handle Shopify section reloads
      document.addEventListener('shopify:section:load', (event) => {
        const section = event.target.querySelector('.events-feed-section');
        if (section) {
          this.initSection(section);
        }
      });
    },

    /**
     * Initialize a single events feed section
     * @param {Element} sectionElement - The section DOM element
     */
    initSection(sectionElement) {
      const sectionId = sectionElement.dataset.sectionId;
      const config = this.extractSectionConfig(sectionElement);

      if (!config.icsUrl) {
        this.showError(sectionId, 'No calendar URL configured');
        return;
      }

      // Store section configuration
      this.sections.set(sectionId, {
        element: sectionElement,
        config: config,
        events: [],
        escapeHandler: null // Store escape key handler for cleanup
      });

      // Start loading events
      this.loadEvents(sectionId);
    },

    /**
     * Extract configuration from section element
     * @param {Element} element - Section element
     * @returns {Object} Configuration object
     */
    extractSectionConfig(element) {
      return {
        icsUrl: element.dataset.icsUrl,
        proxyUrl: element.dataset.proxyUrl,
        maxEvents: parseInt(element.dataset.maxEvents) || 10,
        daysAhead: parseInt(element.dataset.daysAhead) || 365,
        displayMode: element.dataset.displayMode || 'list',
        alignment: element.dataset.alignment || 'center',
        cardWidth: element.dataset.cardWidth || 'auto'
      };
    },

    /**
     * Load events for a section
     * @param {string} sectionId - Section identifier
     */
    async loadEvents(sectionId) {
      const section = this.sections.get(sectionId);
      if (!section) return;

      const { config } = section;

      try {
        this.showLoading(sectionId);

        // Always use proxy worker
        const events = await this.fetchEventsFromProxy(config);

        section.events = events;
        this.renderEvents(sectionId);

      } catch (error) {
        console.error('Error loading events:', error);
        this.showError(sectionId, 'Failed to load events');
      }
    },

    /**
     * Fetch events using the Cloudflare Worker proxy
     * @param {Object} config - Section configuration
     * @returns {Array} Events array
     */
    async fetchEventsFromProxy(config) {
      const url = new URL(config.proxyUrl);
      url.searchParams.set('ics_url', config.icsUrl);
      url.searchParams.set('max_events', config.maxEvents);
      url.searchParams.set('days_ahead', config.daysAhead);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Proxy request failed: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Unknown proxy error');
      }

      return data.events || [];
    },

    /**
     * Render events in the appropriate display mode
     * @param {string} sectionId - Section identifier
     */
    renderEvents(sectionId) {
      const section = this.sections.get(sectionId);
      if (!section) return;

      const { events, config } = section;

      this.hideLoading(sectionId);

      if (events.length === 0) {
        this.showEmpty(sectionId);
        return;
      }

      if (config.displayMode === 'next_only') {
        this.renderNextEventOnly(sectionId, events[0]);
      } else {
        this.renderEventList(sectionId, events);
      }
    },

    /**
     * Render single next event
     * @param {string} sectionId - Section identifier
     * @param {Object} event - Event object
     */
    renderNextEventOnly(sectionId, event) {
      const container = document.getElementById(`next-event-${sectionId}`);
      if (!container) return;

      // Escape IDs to prevent XSS
      const escapedSectionId = this.escapeHtml(sectionId);
      const escapedEventId = this.escapeHtml(event.id);

      container.innerHTML = `
        <div class="next-event-card" onclick="EventsFeed.openModal('${escapedSectionId}', '${escapedEventId}')">
          <div class="next-event-badge">Next Event</div>
          <div class="event-card-date">${this.formatDate(event)}</div>
          <h3 class="event-card-title">${this.escapeHtml(event.title)}</h3>
          <div class="event-card-time">${this.formatTime(event)}</div>
          ${event.location ? `<div class="event-card-location">${this.escapeHtml(event.location)}</div>` : ''}
        </div>
      `;

      container.style.display = 'block';
    },


    /**
     * Render events list
     * @param {string} sectionId - Section identifier
     * @param {Array} events - Events array
     */
    renderEventList(sectionId, events) {
      const container = document.getElementById(`events-container-${sectionId}`);
      if (!container) return;

      const section = this.sections.get(sectionId);
      const { config } = section;

      // Escape sectionId to prevent XSS
      const escapedSectionId = this.escapeHtml(sectionId);

      const eventsHtml = events.map(event => {
        const startDate = this.getEventStartDate(event);
        const day = startDate ? startDate.getDate() : '';
        const month = startDate
          ? startDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()
          : '';

        // Escape event.id to prevent XSS
        const escapedEventId = this.escapeHtml(event.id);

        return `
          <div class="event-card" onclick="EventsFeed.openModal('${escapedSectionId}', '${escapedEventId}')">
            <div class="event-card-date-container">
              <div class="event-card-date">${day}</div>
              <div class="event-card-month">${month}</div>
            </div>
            <div class="event-card-content">
              <h3 class="event-card-title">${this.escapeHtml(event.title)}</h3>
              <div class="event-card-time">${this.formatTime(event)}</div>
              ${event.location ? `<div class="event-card-location">${this.escapeHtml(event.location)}</div>` : ''}
            </div>
          </div>
        `;
      }).join('');

      container.innerHTML = eventsHtml;
      container.setAttribute('data-layout', 'list');
      container.setAttribute('data-alignment', config.alignment);
      container.style.display = 'flex';
    },

    /**
     * Open event details modal
     * @param {string} sectionId - Section identifier
     * @param {string} eventId - Event identifier
     */
    openModal(sectionId, eventId) {
      const section = this.sections.get(sectionId);
      if (!section) {
        console.warn(`Section ${sectionId} not found`);
        return;
      }

      const event = section.events.find(e => e.id === eventId);
      if (!event) {
        console.warn(`Event ${eventId} not found in section ${sectionId}`);
        return;
      }

      // Populate modal content
      const titleEl = document.getElementById(`modal-title-${sectionId}`);
      const dateEl = document.getElementById(`modal-date-${sectionId}`);
      const timeEl = document.getElementById(`modal-time-${sectionId}`);
      const locationEl = document.getElementById(`modal-location-${sectionId}`);
      const descriptionEl = document.getElementById(`modal-description-${sectionId}`);
      const calendarEl = document.getElementById(`modal-calendar-${sectionId}`);
      const directionsEl = document.getElementById(`modal-directions-${sectionId}`);
      const urlEl = document.getElementById(`modal-url-${sectionId}`);

      if (titleEl) titleEl.textContent = event.title;
      if (dateEl) dateEl.textContent = this.formatDate(event);
      if (timeEl) timeEl.textContent = this.formatTime(event);

      // Location
      if (locationEl) {
        if (event.location) {
          locationEl.querySelector('.location-text').textContent = event.location;
          locationEl.style.display = 'block';
        } else {
          locationEl.style.display = 'none';
        }
      }

      // Description
      if (descriptionEl) {
        if (event.description) {
          descriptionEl.querySelector('.description-text').textContent = event.description;
          descriptionEl.style.display = 'block';
        } else {
          descriptionEl.style.display = 'none';
        }
      }

      // Add to calendar link
      if (calendarEl) {
        calendarEl.href = this.generateCalendarUrl(event);
      }

      // Directions link
      if (directionsEl) {
        if (event.location) {
          directionsEl.href = this.generateDirectionsUrl(event.location);
          directionsEl.style.display = 'inline-block';
        } else {
          directionsEl.style.display = 'none';
        }
      }

      // Event URL
      if (urlEl) {
        if (event.url) {
          urlEl.href = event.url;
          urlEl.style.display = 'inline-block';
        } else {
          urlEl.style.display = 'none';
        }
      }

      // Show modal
      const modal = document.getElementById(`events-modal-${sectionId}`);
      if (modal) {
        // Clean up any existing escape handler
        if (section.escapeHandler) {
          document.removeEventListener('keydown', section.escapeHandler);
        }

        modal.style.display = 'flex';
        document.body.style.overflow = 'hidden';

        // Set ARIA attributes for accessibility
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        modal.setAttribute('aria-labelledby', `modal-title-${sectionId}`);

        // Handle escape key
        const handleEscape = (e) => {
          if (e.key === 'Escape') {
            this.closeModal(sectionId);
          }
        };
        document.addEventListener('keydown', handleEscape);
        section.escapeHandler = handleEscape; // Store for cleanup

        // Focus management - focus the close button for accessibility
        const closeButton = modal.querySelector('.events-modal-close');
        if (closeButton) {
          closeButton.focus();
        }
      }
    },

    /**
     * Close event details modal
     * @param {string} sectionId - Section identifier
     */
    closeModal(sectionId) {
      const section = this.sections.get(sectionId);
      const modal = document.getElementById(`events-modal-${sectionId}`);
      
      if (modal) {
        modal.style.display = 'none';
        document.body.style.overflow = '';
        
        // Remove ARIA attributes
        modal.removeAttribute('role');
        modal.removeAttribute('aria-modal');
        modal.removeAttribute('aria-labelledby');
      }

      // Clean up escape key handler
      if (section && section.escapeHandler) {
        document.removeEventListener('keydown', section.escapeHandler);
        section.escapeHandler = null;
      }
    },

    /**
     * Retry loading events after error
     * @param {string} sectionId - Section identifier
     */
    retry(sectionId) {
      this.loadEvents(sectionId);
    },

    /**
     * Show loading state
     * @param {string} sectionId - Section identifier
     */
    showLoading(sectionId) {
      this.hideAll(sectionId);
      const loading = document.getElementById(`events-loading-${sectionId}`);
      if (loading) loading.style.display = 'block';
    },

    /**
     * Hide loading state
     * @param {string} sectionId - Section identifier
     */
    hideLoading(sectionId) {
      const loading = document.getElementById(`events-loading-${sectionId}`);
      if (loading) loading.style.display = 'none';
    },

    /**
     * Show error state
     * @param {string} sectionId - Section identifier
     * @param {string} message - Error message
     */
    showError(sectionId, message = 'Unable to load events') {
      this.hideAll(sectionId);
      const error = document.getElementById(`events-error-${sectionId}`);
      if (error) {
        const messageEl = error.querySelector('.events-error-message');
        if (messageEl) messageEl.textContent = message;
        error.style.display = 'block';
      }
    },

    /**
     * Show empty state
     * @param {string} sectionId - Section identifier
     */
    showEmpty(sectionId) {
      this.hideAll(sectionId);
      const empty = document.getElementById(`events-empty-${sectionId}`);
      if (empty) empty.style.display = 'block';
    },

    /**
     * Hide all state elements
     * @param {string} sectionId - Section identifier
     */
    hideAll(sectionId) {
      const elements = [
        `events-loading-${sectionId}`,
        `events-error-${sectionId}`,
        `events-container-${sectionId}`,
        `next-event-${sectionId}`,
        `events-empty-${sectionId}`
      ];

      elements.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.style.display = 'none';
      });
    },

    /**
     * Check whether an event date should be treated as a local calendar date
     * @param {string|null} dateString - Event date value
     * @param {string|null} dateType - Event date type
     * @returns {boolean} Whether the date is date-only
     */
    isDateOnlyValue(dateString, dateType, event = null) {
      return Boolean(dateString) && (
        dateType === 'date' ||
        /^\d{4}-\d{2}-\d{2}$/.test(dateString) ||
        (
          event?.isAllDay &&
          /^\d{4}-\d{2}-\d{2}T00:00:00(?:\.000)?Z$/.test(dateString)
        )
      );
    },

    /**
     * Parse a YYYY-MM-DD date as a local Date without timezone conversion
     * @param {string} dateString - Date-only value
     * @returns {Date|null} Parsed date
     */
    parseDateOnly(dateString) {
      const match = String(dateString).match(
        /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.000)?Z)?$/
      );
      if (!match) return null;

      const [, year, month, day] = match;
      return new Date(Number(year), Number(month) - 1, Number(day));
    },

    /**
     * Parse an event date using the correct ICS semantics
     * @param {string|null} dateString - Event date value
     * @param {string|null} dateType - Event date type
     * @returns {Date|null} Parsed date
     */
    parseEventDate(dateString, dateType, event = null) {
      if (!dateString) return null;

      if (this.isDateOnlyValue(dateString, dateType, event)) {
        return this.parseDateOnly(dateString);
      }

      const date = new Date(dateString);
      return Number.isNaN(date.getTime()) ? null : date;
    },

    /**
     * Get the parsed start date for an event
     * @param {Object} event - Event object
     * @returns {Date|null} Parsed start date
     */
    getEventStartDate(event) {
      return this.parseEventDate(event.startDate, event.startDateType, event);
    },

    /**
     * Get the parsed end date for an event
     * @param {Object} event - Event object
     * @param {Object} options - Parsing options
     * @returns {Date|null} Parsed end date
     */
    getEventEndDate(event, options = {}) {
      const endDate = this.parseEventDate(event.endDate, event.endDateType, event);
      if (!endDate) return null;

      if (
        options.display &&
        event.isAllDay &&
        this.isDateOnlyValue(event.endDate, event.endDateType, event)
      ) {
        return new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() - 1);
      }

      return endDate;
    },

    /**
     * Format a date-only value for Google Calendar
     * @param {string|Date} value - Date-only value
     * @returns {string} Google Calendar date
     */
    formatDateOnlyValue(value) {
      if (value instanceof Date) {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        return `${year}${month}${day}`;
      }

      const parsedDate = this.parseDateOnly(value);
      if (parsedDate) {
        return this.formatDateOnlyValue(parsedDate);
      }

      return String(value || '').replace(/-/g, '');
    },

    /**
     * Add days to a date using local calendar math
     * @param {Date} date - Base date
     * @param {number} days - Number of days to add
     * @returns {Date} Updated date
     */
    addDays(date, days) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
    },

    /**
     * Format date for display
     * @param {Object} event - Event object
     * @returns {string} Formatted date
     */
    formatDate(event) {
      const startDate = this.getEventStartDate(event);
      if (!startDate) return '';

      const endDate = this.getEventEndDate(event, { display: true });
      const formatOptions = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      };

      const startLabel = startDate.toLocaleDateString('en-US', formatOptions);
      if (!endDate || startDate.toDateString() === endDate.toDateString()) {
        return startLabel;
      }

      return `${startLabel} - ${endDate.toLocaleDateString('en-US', formatOptions)}`;
    },

    /**
     * Format time for display
     * @param {Object} event - Event object
     * @returns {string} Formatted time
     */
    formatTime(event) {
      if (event.isAllDay) {
        return 'All day';
      }

      const start = this.getEventStartDate(event);
      if (!start) return '';

      const timeOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };

      let timeStr = start.toLocaleTimeString('en-US', timeOptions);

      if (event.endDate) {
        const end = this.getEventEndDate(event);
        if (end) {
          const endStr = end.toLocaleTimeString('en-US', timeOptions);
          timeStr += ` - ${endStr}`;
        }
      }

      return timeStr;
    },

    /**
     * Generate Google Calendar URL for event
     * @param {Object} event - Event object
     * @returns {string} Google Calendar URL
     */
    generateCalendarUrl(event) {
      const start = this.getEventStartDate(event);
      if (!start) {
        return 'https://calendar.google.com/calendar/render';
      }

      const formatDateForGoogle = (date) => {
        return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
      };

      let dates;
      if (event.isAllDay && this.isDateOnlyValue(event.startDate, event.startDateType, event)) {
        const startDate = this.formatDateOnlyValue(event.startDate);
        const endDate = event.endDate && this.isDateOnlyValue(event.endDate, event.endDateType, event)
          ? this.formatDateOnlyValue(event.endDate)
          : this.formatDateOnlyValue(this.addDays(start, 1));

        dates = `${startDate}/${endDate}`;
      } else {
        const end = event.endDate
          ? this.getEventEndDate(event)
          : new Date(start.getTime() + 60 * 60 * 1000);

        dates = `${formatDateForGoogle(start)}/${formatDateForGoogle(end)}`;
      }

      const params = new URLSearchParams({
        action: 'TEMPLATE',
        text: event.title,
        dates,
        details: event.description || '',
        location: event.location || ''
      });

      return `https://calendar.google.com/calendar/render?${params.toString()}`;
    },

    /**
     * Generate Google Maps directions URL
     * @param {string} location - Location string
     * @returns {string} Google Maps URL
     */
    generateDirectionsUrl(location) {
      const params = new URLSearchParams({
        api: '1',
        destination: location
      });

      return `https://www.google.com/maps/dir/?${params.toString()}`;
    },

    /**
     * Escape HTML characters
     * @param {string} text - Text to escape
     * @returns {string} Escaped text
     */
    escapeHtml(text) {
      if (!text) return '';

      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  };

  // Expose methods globally for onclick handlers
  window.EventsFeed.openModal = EventsFeed.openModal.bind(EventsFeed);
  window.EventsFeed.closeModal = EventsFeed.closeModal.bind(EventsFeed);
  window.EventsFeed.retry = EventsFeed.retry.bind(EventsFeed);

  // Initialize when DOM is ready
  EventsFeed.init();

})();
